/* ChannelsIntelligenceTab — Bundle 2.9 (5 May 2026).
 * Stays-only standalone Channels tab. Reuses the same booking-source
 * data the Occupancy tab shows in its "Source Mix" card, but as a
 * dedicated tab so Dipali can drill into Direct vs Booking.com vs
 * MakeMyTrip vs OTA splits with switchable date windows.
 *
 * Source: /api/outlet-intel/stays/channels/summary
 * (also live from `ezee_stays_folios`, Rule 16).
 */
import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { ShieldCheck, AlertCircle } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const RANGE_OPTIONS = [
  { id: 7,  label: '7d' },
  { id: 30, label: '30d' },
  { id: 90, label: '90d' },
];

// Crude commission heuristic (PMS doesn't carry commission today). Used
// to flag "OTA drag" — a directional signal, not an accounting number.
const COMMISSION_HINT = {
  'direct': 0,
  'booking.com': 15,
  'makemytrip': 17,
  'goibibo': 17,
  'agoda': 17,
  'expedia': 18,
  'airbnb': 14,
  'walk-in': 0,
  'corporate': 5,
};

const CHANNEL_COLOR = {
  'direct': '#10B981',
  'booking.com': '#3B82F6',
  'makemytrip': '#F59E0B',
  'goibibo': '#EF4444',
  'agoda': '#A855F7',
  'expedia': '#06B6D4',
  'airbnb': '#EC4899',
  'walk-in': '#84CC16',
  'corporate': '#6366F1',
};

const channelKey = (s) => (s || '').toLowerCase().trim();
const commissionFor = (channel) => COMMISSION_HINT[channelKey(channel)] ?? 0;
const colorFor = (channel) => CHANNEL_COLOR[channelKey(channel)] || '#6B7280';

export default function ChannelsIntelligenceTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/outlet-intel/stays/channels/summary?days=${days}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading channels intelligence…</div>;
  if (error || !data) return <div className="text-sm" style={{ color: '#DC2626' }}>Failed to load: {error || 'no data'}</div>;

  const rows = data.rows || [];
  const totalRev = data.total_revenue || 0;
  const totalFolios = data.total_folios || 0;
  const directRow = rows.find(r => channelKey(r.channel) === 'direct');
  const directShare = directRow?.share_pct || 0;
  const otaRows = rows.filter(r => commissionFor(r.channel) > 0);
  const otaDrag = otaRows.reduce((acc, r) => acc + (r.revenue * commissionFor(r.channel) / 100), 0);

  return (
    <div className="space-y-4" data-testid="channels-intel">
      {/* Range switcher + headline */}
      <div className="flex items-center justify-between">
        <div className="text-[12px]" style={{ color: '#6B7280' }}>
          {data.outlet_name} · booking channel mix
        </div>
        <div className="inline-flex rounded-lg p-0.5" style={{ backgroundColor: '#F3F4F6' }}>
          {RANGE_OPTIONS.map(r => (
            <button
              key={r.id}
              onClick={() => setDays(r.id)}
              data-testid={`channels-range-${r.label}`}
              className="text-[11px] px-3 py-1 rounded-md font-medium transition"
              style={{
                backgroundColor: days === r.id ? 'white' : 'transparent',
                color: days === r.id ? '#1B2A4A' : '#6B7280',
                boxShadow: days === r.id ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top-line cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={`Folios · ${days}d`} value={totalFolios.toLocaleString('en-IN')}
                 sub={`${rows.length} channel${rows.length === 1 ? '' : 's'}`} testid="kpi-channels-folios" />
        <KpiCard label="Pure Room Rev" value={INR(totalRev)}
                 sub="Rule 16 applied" highlight testid="kpi-channels-rev" />
        <KpiCard label="Direct share"
                 value={`${directShare}%`}
                 sub={directRow ? `${directRow.folios} folios · ${INR(directRow.revenue)}` : 'no direct bookings'}
                 testid="kpi-channels-direct" />
        <KpiCard label="OTA commission drag (est.)" value={INR(otaDrag)}
                 sub="Rough — PMS has no live commission" testid="kpi-channels-drag" />
      </div>

      {/* Stacked share bar */}
      <Card title="Revenue share" testid="card-channels-bar">
        {totalRev > 0 ? (
          <div className="flex w-full h-7 rounded-full overflow-hidden" style={{ border: '1px solid #E5E1DC' }}>
            {rows.map(r => (
              <div
                key={r.channel}
                title={`${r.channel}: ${r.share_pct}% · ${INR(r.revenue)}`}
                style={{ width: `${r.share_pct}%`, backgroundColor: colorFor(r.channel) }}
                data-testid={`channel-bar-${channelKey(r.channel).replace(/[^a-z0-9]/g, '-')}`}
                className="flex items-center justify-center text-[10px] text-white font-medium"
              >
                {r.share_pct >= 8 && r.channel}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px]" style={{ color: '#6B7280' }}>No bookings in this window.</p>
        )}
      </Card>

      {/* Per-channel rows */}
      <Card title={`Channel breakdown · ${days}d`} testid="card-channels-table">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ color: '#6B7280' }} className="text-[10px] uppercase tracking-wide">
                <th className="text-left py-2">Channel</th>
                <th className="text-right">Folios</th>
                <th className="text-right">Pure Room Rev</th>
                <th className="text-right">Share</th>
                <th className="text-right">Avg / folio</th>
                <th className="text-right">Comm. (est.)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const comm = commissionFor(r.channel);
                const drag = (r.revenue * comm) / 100;
                return (
                  <tr key={r.channel} style={{ borderTop: '1px solid #F3F4F6' }}
                      data-testid={`channel-row-${channelKey(r.channel).replace(/[^a-z0-9]/g, '-')}`}>
                    <td className="py-2 capitalize">
                      <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                            style={{ backgroundColor: colorFor(r.channel) }} />
                      {r.channel}
                      {channelKey(r.channel) === 'direct' && (
                        <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}>
                          <ShieldCheck size={9} className="inline mr-0.5" /> no commission
                        </span>
                      )}
                    </td>
                    <td className="text-right">{r.folios.toLocaleString('en-IN')}</td>
                    <td className="text-right font-semibold">{INR(r.revenue)}</td>
                    <td className="text-right">{r.share_pct}%</td>
                    <td className="text-right">{INR(r.avg_per_folio)}</td>
                    <td className="text-right" style={{ color: drag > 0 ? '#DC2626' : '#9CA3AF' }}>
                      {comm > 0 ? `${INR(drag)} (${comm}%)` : '—'}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan="6" className="py-4 text-center" style={{ color: '#9CA3AF' }}>
                  No channel data in this window.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Lift signal */}
      {directShare < 30 && totalRev > 0 && (
        <div className="rounded-lg p-3 text-[12px] flex items-start gap-2"
             style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
             data-testid="channels-direct-warning">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Direct share is <strong>{directShare}%</strong>. Every 5 percentage
            points shifted from OTAs to Direct saves roughly{' '}
            <strong>{INR(totalRev * 0.05 * 0.15)}</strong> in commission over
            this window. Worth a Direct-rate-parity push.
          </span>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, highlight, testid }) {
  const bg = highlight ? '#1B2A4A' : 'white';
  const fg = highlight ? 'white' : '#1B2A4A';
  const muted = highlight ? '#9DB5CC' : '#6B7280';
  return (
    <div className="rounded-xl p-4"
         style={{ backgroundColor: bg, color: fg, border: highlight ? 'none' : '1px solid #E5E1DC' }}
         data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: muted }}>{label}</p>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-[11px] mt-1" style={{ color: muted }}>{sub}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}
